import { useEffect, useState } from 'react';
import { LogIn } from 'lucide-react';
import AppLogo from './AppLogo';
import ThemeToggle from './ThemeToggle';
import LegalDisclaimer from './LegalDisclaimer';
import { APP_NAME, APP_TAGLINE } from './productMeta';
import { getApiBase } from './apiBase';

const DEFAULT_SIGNUP_URL = 'https://www.slpmicrosystems.com/signup';

export default function ToolLogin({ onSuccess, uiTheme, onThemeChange }) {
  const [mode, setMode] = useState('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [allowSignup, setAllowSignup] = useState(false);
  const [signupUrl, setSignupUrl] = useState(DEFAULT_SIGNUP_URL);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [otp, setOtp] = useState('');

  useEffect(() => {
    const API_BASE = getApiBase();
    fetch(`${API_BASE}/auth/config`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data) return;
        if (typeof data.allowSignup === 'boolean') setAllowSignup(data.allowSignup);
        if (data.signupUrl) setSignupUrl(data.signupUrl);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!allowSignup && mode === 'signup') setMode('login');
  }, [allowSignup, mode]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!username.trim() || !password) {
      setError('Username and password required');
      return;
    }
    setLoading(true);
    try {
      const API_BASE = getApiBase();
      const url = `${API_BASE}/auth/tool/${mode === 'signup' ? 'register' : 'login'}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          username: username.trim(),
          password,
          ...(mode === 'login' && mfaRequired ? { otp: otp.trim() } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      // Two-factor challenge (either the initial prompt or an invalid code).
      if (mode === 'login' && data.mfaRequired) {
        setMfaRequired(true);
        if (data.error) setError(data.error);
        else setInfo('Enter the 6-digit code from your authenticator app (or a backup code).');
        return;
      }
      if (!res.ok) {
        if (data.signupUrl) {
          setError(data.error || 'Create your SLP account on the website first.');
          setInfo(`Open ${data.signupUrl} to register. Email verification and SLP approval are required.`);
          return;
        }
        setError(data.error || `Request failed (${res.status})`);
        return;
      }
      // Local/dev register: account created but not logged in.
      if (mode === 'signup' && (data.needsLogin || !data.user)) {
        setMode('login');
        setInfo(data.message || 'Account created. Sign in with your username and password.');
        setPassword('');
        return;
      }
      const meRes = await fetch(`${API_BASE}/auth/me`, { credentials: 'include' });
      if (!meRes.ok) {
        setError('Sign-in did not complete. Try again, or ask your consultant if the problem continues.');
        return;
      }
      onSuccess?.(data.user);
    } catch (err) {
      setError(
        err?.message === 'Failed to fetch'
          ? 'Cannot reach the application. Refresh the page or try again in a few minutes.'
          : err.message
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-shell relative flex min-h-screen items-center justify-center p-4">
      <div className="app-shell-backdrop" aria-hidden="true" />
      <div className="card-panel relative w-full max-w-md p-8 shadow-2xl">
        <div className="mb-6 text-center">
          <AppLogo size={56} className="mx-auto" />
          <h1 className="mt-4 text-2xl font-bold text-slate-100">{APP_NAME}</h1>
          <p className="mt-2 text-sm text-slate-400">{APP_TAGLINE}</p>
        </div>
        <ul className="mb-5 grid gap-1.5 text-left text-xs text-slate-400">
          <li>Oracle element entries vs ADP payroll register</li>
          <li>Pre-payroll: banking, email, SSL, dates, time</li>
          <li>Live Fusion pulls and exception export to Excel</li>
        </ul>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-300">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="input-field"
              autoComplete="username"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-300">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field"
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            />
          </div>
          {mode === 'login' && mfaRequired && (
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-300">
                Authentication code
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="input-field"
                autoComplete="one-time-code"
                placeholder="6-digit code or backup code"
                autoFocus
              />
            </div>
          )}
          {error && <p className="alert-error text-sm">{error}</p>}
          {info && <p className="text-sm text-emerald-300/90">{info}</p>}
          <button type="submit" disabled={loading} className="btn-primary w-full py-2.5">
            <LogIn className="h-4 w-4" />
            {loading
              ? 'Please wait…'
              : mode === 'signup'
                ? 'Create account'
                : mfaRequired
                  ? 'Verify'
                  : 'Sign in'}
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-slate-400">
          {allowSignup ? (
            mode === 'signup' ? (
              <>
                Have an account?{' '}
                <button type="button" className="link-accent" onClick={() => setMode('login')}>
                  Sign in
                </button>
              </>
            ) : (
              <>
                New user?{' '}
                <button type="button" className="link-accent" onClick={() => setMode('signup')}>
                  Sign up
                </button>
              </>
            )
          ) : (
            <>
              New user?{' '}
              <a href={signupUrl} className="link-accent">
                Create your SLP account
              </a>
              <span className="mt-2 block text-xs text-slate-500">
                Email verification and SLP approval are required before you can use this app.
              </span>
            </>
          )}
        </p>
        <div className="mt-4">
          <ThemeToggle theme={uiTheme} onChange={onThemeChange} />
        </div>
        <div className="mt-5">
          <LegalDisclaimer compact />
        </div>
      </div>
    </div>
  );
}
